import cn from "classnames"
import { Fragment, useEffect, useRef, useState } from "react"

import { Badge, Checkbox } from "@/atoms"
import { useFilters, useStateFilter } from "@/hooks/useFilters"
import { useLogger } from "@/hooks/useLogger"
import { DropdownFilter } from "../DropdownFilter"

import styles from "./CheckboxFilter.module.scss"

import type { MenuItemGroupType, MenuProps } from "@/atoms"
import type { CheckboxFilterProps } from "."

// Called from event handlers only, never while rendering.
const getTimestamp = () => Date.now()

export const CheckboxFilter = (props: CheckboxFilterProps) => {
  const {
    keyFilter,
    isLoading,
    items = [],
    placeHolderDefaultValue,
    order,
    placement,
    onApply,
    getGroups,
    renderOptionSuffix,
    labelSuffix,
    t
  } = props
  const { searchParams, setSearchParams } = useFilters()
  const { logger, EVENTS } = useLogger()
  const refGroup = useRef<{
    groups: MenuItemGroupType[]
    value: string | null
  } | null>(null)
  const modifiedByGroup = t && getGroups

  if (modifiedByGroup && refGroup.current === null)
    refGroup.current = {
      groups: getGroups(t, undefined, placement) || [],
      value: null
    }

  const createInitialFilterState = (obj = searchParams) => {
    return (obj.get(keyFilter) || "")
      .split(",")
      .filter((v) => !!v)
      .reduce(
        (acc, key) => {
          acc[key] = { checked: true, timestamp: Date.now() }

          return acc
        },
        {} as Record<string, any>
      )
  }

  const onChangeState = (
    prev: Record<string, string> | URLSearchParams,
    values: Record<string, { checked: boolean; timestamp: number }>
  ) => {
    const next = new URLSearchParams(prev)

    const nextValue = Object.keys(values)
      .filter((key) => !!values[key].checked)
      .join(",")

    if (!nextValue) next.delete(keyFilter)
    else next.set(keyFilter, nextValue)

    return next
  }
  const { filterState, reset, onFilterStateChange, markDirty, onClear, onOk } =
    useStateFilter(createInitialFilterState(), {
      onChangeState,
      setSearchParams
    })

  const [placeHolder, setPlaceHolder] = useState({
    label: placeHolderDefaultValue,
    count: 0
  })

  const onChange = (name: string, checked: boolean) => {
    onFilterStateChange({
      [name]: { checked, timestamp: getTimestamp() }
    })

    logger.info(EVENTS.ANALYTIC_EVENTS.TAB.FILTER.QUICK_FILTERS, {
      placement,
      name: keyFilter,
      value: checked
    })
  }

  const menuItems: MenuProps["items"] = items
    .sort((a, b) => {
      if (order === "none") return 0
      if (order === "desc") return b.label.localeCompare(a.label)

      return a.label.localeCompare(b.label)
    })
    .map((option) => {
      const { label, value, displayLabel = label } = option
      const checked = !!filterState.values[value]?.checked

      return {
        className: cn(styles.checkboxMenuItem, checked && styles.checked),
        label: (
          <Checkbox
            className={styles.checkbox}
            onChange={(e) => {
              onChange(value, !!e.target.checked)
            }}
            checked={checked}
          >
            {displayLabel}
            {renderOptionSuffix?.(option)}
          </Checkbox>
        ),
        sort: label,
        key: value,
        onKeyPress: (event: React.KeyboardEvent<HTMLDivElement>) => {
          if (event.code === "Space") onChange(value, !checked)
        }
      }
    })

  useEffect(() => {
    const initialState = createInitialFilterState(searchParams)

    const itemsByValue = items.reduce(
      (acc, { label, value, displayLabel = label }) => {
        acc[value] = displayLabel

        return acc
      },
      {} as Record<string, string>
    )
    const keys = Object.keys(initialState)
      .filter((key) => !!initialState[key]?.checked)
      .sort((a, b) => initialState[a].timestamp - initialState[b].timestamp)
      .map((key) => itemsByValue[key])

    let groupCount = 0

    if (refGroup.current) {
      const groups = refGroup.current.groups
        .flatMap((group) =>
          group?.children?.map((child) => {
            const { key } = child || {}

            if (!key) return ""

            const value = searchParams.get(`${key}`)

            if (!value || value !== "YES") return ""

            return key
          })
        )
        .sort()
      const groupString = groups.join("")

      if (groupString !== refGroup.current.value) {
        if (!filterState.changed && refGroup.current.value !== null) markDirty()

        refGroup.current.value = groupString
      }

      groupCount = groups.filter((v) => !!v).length
    }

    const keyCount = keys.length

    setPlaceHolder({
      label: keys?.[0] || placeHolderDefaultValue,
      count: keyCount + (keyCount === 0 ? groupCount + 1 : groupCount)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isLoading])

  useEffect(() => {
    const result = menuItems.some(({ key }: any) => {
      const checked = !!filterState.values[key]?.checked
      const checkedInUrl = !!searchParams
        .get(keyFilter)
        ?.split(",")
        .includes(key)

      return checked !== checkedInUrl
    })

    if (result) {
      reset(createInitialFilterState())
    }

    onOk()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const Wrapper = props.allowApply ? "div" : Fragment

  return (
    <DropdownFilter
      allowSearch
      allowApply={props.allowApply}
      disabled={isLoading}
      changed={filterState.changed}
      isPlaceholder={placeHolder.label === placeHolderDefaultValue}
      clearDisabled={!filterState.allowClear}
      okDisabled={!filterState.dirty}
      okHide={!onApply}
      keyFilter={keyFilter}
      menuItems={
        modifiedByGroup ? getGroups(t, menuItems, placement) : menuItems
      }
      onClear={onClear}
      onOk={() => onApply?.(onChangeState(searchParams, filterState.values))}
      onOpenChange={(open: boolean) => {
        if (open) {
          reset(createInitialFilterState(), false)
        }
      }}
    >
      <Wrapper>
        {placeHolder.label}
        {labelSuffix}
        {placeHolder.count > 1 && (
          <Badge
            className={cn(styles.badge, isLoading && styles.disabled)}
            count={`+ ${placeHolder.count - 1}`}
          />
        )}
      </Wrapper>
    </DropdownFilter>
  )
}
